import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, X, Clock, MinusCircle, Search } from "lucide-react";
import {
  adminListBookings,
  adminUpdateBookingStatus,
  adminTestSms,
} from "@/lib/api/portal.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/_authenticated/portal/admin/bookings")({
  component: AdminBookings,
});

const STATUSES = ["pending", "confirmed", "completed", "cancelled"] as const;

function AdminBookings() {
  const fetchAll = useServerFn(adminListBookings);
  const updateStatus = useServerFn(adminUpdateBookingStatus);
  const testSms = useServerFn(adminTestSms);
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-bookings"],
    queryFn: () => fetchAll(),
  });
  const [smsTo, setSmsTo] = useState("+27712766198");
  const [smsResult, setSmsResult] = useState<string>("");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const filtered = useMemo(() => {
    const list: any[] = data?.bookings ?? [];
    return list.filter((b) => {
      if (statusFilter !== "all" && b.status !== statusFilter) return false;
      if (!q.trim()) return true;
      const needle = q.toLowerCase();
      return [b.full_name, b.email, b.phone, b.service, b.preferred_date]
        .filter(Boolean)
        .some((v: string) => String(v).toLowerCase().includes(needle));
    });
  }, [data, q, statusFilter]);

  const pendingCount = (data?.bookings ?? []).filter((b: any) => b.status === "pending").length;

  async function change(id: string, status: string) {
    try {
      await updateStatus({ data: { id, status: status as any } });
      toast.success("Updated");
      qc.invalidateQueries({ queryKey: ["admin-bookings"] });
    } catch (e: any) {
      toast.error("Update failed", { description: e?.message });
    }
  }

  async function runSmsTest() {
    setSmsResult("Sending…");
    try {
      const r = await testSms({ data: { to: smsTo } });
      setSmsResult(JSON.stringify(r, null, 2));
      if (r.ok) toast.success("SMS accepted by Clickatell");
      else toast.error("SMS failed", { description: r.error ?? "see details" });
    } catch (e: any) {
      setSmsResult(String(e?.message ?? e));
    }
  }

  if (error) return <p className="text-sm text-red-600">Forbidden or load failed.</p>;

  return (
    <section className="space-y-8">
      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-xl text-navy">
            All bookings
            {pendingCount > 0 && (
              <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                {pendingCount} pending alerts
              </span>
            )}
          </h2>
          <div className="flex flex-wrap gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search client, email, phone…"
                className="w-64 rounded-md border border-input bg-background pl-8 pr-3 py-2 text-sm focus:border-gold focus:outline-none"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-md border border-input bg-background px-2 py-2 text-sm"
            >
              <option value="all">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        <p className="mb-2 text-xs text-muted-foreground">
          {filtered.length} of {data?.bookings.length ?? 0} bookings
        </p>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Date / Time</th>
                <th className="px-3 py-2 text-left">Client</th>
                <th className="px-3 py-2 text-left">Service</th>
                <th className="px-3 py-2 text-left">Contact</th>
                <th className="px-3 py-2 text-left">Notifications</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b: any) => (
                <tr key={b.id} className="border-t border-border">
                  <td className="px-3 py-2 align-top">
                    <div>{b.preferred_date}</div>
                    <div className="text-xs text-muted-foreground">{b.preferred_time} · {b.consultation_type}</div>
                  </td>
                  <td className="px-3 py-2 align-top">{b.full_name}</td>
                  <td className="px-3 py-2 align-top">{b.service}</td>
                  <td className="px-3 py-2 align-top">
                    <div>{b.phone}</div>
                    <div className="text-xs text-muted-foreground">{b.email}</div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <SmsChecklist
                      adminStatus={b.admin_sms_status}
                      adminError={b.admin_sms_error}
                      clientStatus={b.client_sms_status}
                      clientError={b.client_sms_error}
                      sentAt={b.sms_sent_at}
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <select
                      value={b.status}
                      onChange={(e) => change(b.id, e.target.value)}
                      className="rounded border border-input bg-background px-2 py-1 text-xs"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
              {data && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No bookings match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="font-display text-lg text-navy">SMS delivery test</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Sends a single test SMS via Clickatell and shows the raw response — use this to diagnose delivery issues.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Input value={smsTo} onChange={(e) => setSmsTo(e.target.value)} className="max-w-xs" />
          <Button onClick={runSmsTest} className="bg-navy hover:bg-navy/90">Send test SMS</Button>
        </div>
        {smsResult && (
          <pre className="mt-3 overflow-x-auto rounded bg-muted p-3 text-[11px]">{smsResult}</pre>
        )}
      </div>
    </section>
  );
}

function SmsChecklist({
  adminStatus,
  adminError,
  clientStatus,
  clientError,
  sentAt,
}: {
  adminStatus?: string | null;
  adminError?: string | null;
  clientStatus?: string | null;
  clientError?: string | null;
  sentAt?: string | null;
}) {
  return (
    <div className="space-y-1 text-xs">
      <Row label="Admin SMS" status={adminStatus} error={adminError} />
      <Row label="Client SMS" status={clientStatus} error={clientError} />
      {sentAt && (
        <div className="text-[10px] text-muted-foreground">
          {new Date(sentAt).toLocaleString()}
        </div>
      )}
    </div>
  );
}

function Row({
  label,
  status,
  error,
}: {
  label: string;
  status?: string | null;
  error?: string | null;
}) {
  const s = (status ?? "pending").toLowerCase();
  const Icon =
    s === "sent" ? Check : s === "failed" ? X : s === "skipped" ? MinusCircle : Clock;
  const color =
    s === "sent"
      ? "text-emerald-600"
      : s === "failed"
        ? "text-red-600"
        : s === "skipped"
          ? "text-muted-foreground"
          : "text-amber-600";
  return (
    <div className="flex items-center gap-1.5" title={error ?? undefined}>
      <Icon className={`h-3.5 w-3.5 ${color}`} />
      <span className="font-medium text-foreground/80">{label}</span>
      <span className={`${color} capitalize`}>{s}</span>
    </div>
  );
}
